'use strict';
import React, {Component} from 'react';
import {observer} from "mobx-react";
import PropTypes from 'prop-types';
import moment from 'moment';
import classnames from 'classnames';

import Button from "react-bootstrap/Button";
import Card from 'react-bootstrap/Card';
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

import termStore from '@uw-it-sis/lib-react/lib/TermStore';
import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {isTrue, isFalse, isEmpty} from '@uw-it-sis/lib-react/lib/Utils';
import registrationDateStore from '@uw-it-sis/lib-react-myplan/lib/RegistrationDateStore';
import {buildRegistrationSlnList, buildRegistrationUrl} from '@uw-it-sis/lib-react-myplan/lib/RegistrationUtils';
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils';
import SimpleState from '@uw-it-sis/lib-react/lib/SimpleState';

import registrationDetailsStore from './RegistrationDetailsStore';

/**
 * The registration button and surrounding widgetry.
 */
class RegistrationButton extends Component {

    constructor(props) {
        super(props);
        this.props = props;
        this.analyticsStore = appStore.getAnalyticsStore();
        this.handleAnalyticsAdd = this.handleAnalyticsAdd.bind(this);
        this.handleRegisterButtonClick = this.handleRegisterButtonClick.bind(this);
        this.buildDoneComponent = this.buildDoneComponent.bind(this);
    }

    componentDidMount() {
        registrationDateStore.mount();
    }

    componentWillUnmount() {
        registrationDateStore.unmount();
    }

    handleRegisterButtonClick() {
        const dataStateSummary = registrationDetailsStore.getCurrentDataStateSummary();
        const selectedCount = registrationDetailsStore.getSelectedRegistrationItemsCount();
        let additionalData = {
            regdCnt: dataStateSummary.registeredCount,
            cIssuesCnt: dataStateSummary.courseIssuesCount,
            sIssuesCnt: dataStateSummary.sectionIssuesCount,
            readyCnt: dataStateSummary.readyCount,
            holdsCnt: dataStateSummary.holdsCount,
            regDate: dataStateSummary.registrationDate,
            selCnt: selectedCount,
            isSeamless: `false`
        }
        this.analyticsStore.addEvent("RegPrev-RegButtonClick", {}, additionalData);

        this.props.showModal();
    }

    /**
     * Adds analytics for link clicks.
     *
     * @param eventKey The key name for the link clicked.
     */
    handleAnalyticsAdd(eventKey) {
        this.analyticsStore.addEvent(eventKey,{},{});
    }

    /**
     * Find the set of course objects from the list of ready issues that have been selected to be sent to the registration page
     *
     * @returns {Array}
     * @private
     */
    _findSelectedCourses() {
        let selectedCourses = [];

        this.props.readyList.map((course, i) => {
            if (isTrue(course['sendToRegistration'])) {
                selectedCourses.push(course);
            }
        });

        return selectedCourses;
    }

    /**
     * Build the URL for the registration button based on the given set of courses from RegistrationDetailsStore
     *
     * @param selectedCourses A list of items from RegistrationDetailsStore, ideally the result of running _findSelectedCourses()
     * @returns {string}
     * @private
     */
    _calculateUrl(selectedCourses) {

        let slns = buildRegistrationSlnList(selectedCourses);

        let slnsToEntryCodes = {};

        this.props.readyList.map((course, i) => {
            course.planActivities.map(function (activity, j) {
                let sln = activity['registrationCode'];
                slnsToEntryCodes[sln] = activity['entryCode'];
            });
        });

        let baseRegUrl = appStore.getAppAttribute('sdb_registration_url');

        return buildRegistrationUrl(baseRegUrl, appStore.getStudentSystemKey(),
            this.props.selectedTermId, slns, slnsToEntryCodes, "mp_reg_preview");
    }

    buildDoneComponent() {

        // Get the term data for the selected term id (via component prop)
        let selectedTerm = termStore.get('publishedTerms').find(function (term) {
            return term.id === this.props.selectedTermId;
        }.bind(this));

        let registrationDateMap = undefined;

        if (selectedTerm) {
            registrationDateMap = registrationDateStore.getRegistrationDataForTerm(selectedTerm.codeName);
        }

        let formattedRegistrationDate = '';

        if (!isEmpty(registrationDateMap)) {
            formattedRegistrationDate = registrationDateMap.date.format("dddd, MMM D");
        }

        let registrationOpen = false;
        let now = moment(appStore.getNow());

        let lastAddDate = undefined;
        let firstRegPeriodDate = TermUtils.findPeriodOnePriorityRegDate(selectedTerm);

        if (selectedTerm) {
            selectedTerm.academicCalendar.forEach( function(entry) {

                if (entry.key === 'last_add_day') {
                    // use a time of 11:59pm to check that today's date is past the complete day of the last add day
                    lastAddDate = moment(entry.date + " 23:59", "YYYY-MM-DD hh:mm");
                }
            });
        }

        let isPastSelectedTermLastAddDate = false;
        if (!isEmpty(lastAddDate)) {
            isPastSelectedTermLastAddDate = now.isAfter(lastAddDate);
        }

        if ( !isPastSelectedTermLastAddDate ) {
            // get the registration date moment as a separate instance
            // let registrationDate = moment(registrationDateMap.date);
            registrationOpen = now.isAfter(firstRegPeriodDate);
        }

        let buttonIssues = null;
        if ( this.props.hasIssues && isFalse(this.props.hasReady) ) {
            buttonIssues = <p>Resolve issues with your plan items in order to add them to your registration cart.</p>;
        }

        let selectedCourses = this._findSelectedCourses();

        // Disable button if in adviser mode or if registration is not open,
        // or if there are no ready items, or if there are no selected items to send to registration
        let disableButton = ( appStore.isAdviser() || isFalse(registrationOpen) || isFalse(this.props.hasReady) || isEmpty(selectedCourses));

        let buttonUrl = "";

        let buttonRegister =
            <span className={classnames("btn btn-primary btn-inline", {"disabled": disableButton})}
                  aria-label={disableButton ? "Registration Not Ready":"Send to Registration"}>
                Send to Registration
            </span>;

        if (!disableButton) {
            buttonUrl = this._calculateUrl(selectedCourses);
            buttonRegister =
                <a href={buttonUrl}
                   target="_blank"
                   aria-label={disableButton ? "Registration Not Ready":"Send to Registration"}
                   className={classnames("btn btn-primary btn-inline", {"disabled": disableButton})}
                   onClick={this.handleRegisterButtonClick}>Send to Registration</a>;
        }

        let buttonComponent =
            <div>
                {buttonIssues}
                {buttonRegister}
            </div>;

        let registrationTiming;

        if ( registrationOpen ) {
            registrationTiming =
                <div>
                    <p>{registrationDateMap.isPeriodOne && now.isBefore(registrationDateMap.date) ? 'Your estimated registration date is ' + formattedRegistrationDate + ' at 6:00 AM.' :'Registration is open.'}</p>
                    <p>You will be able to register online until {lastAddDate.format("dddd, MMM D")}.</p>
                </div>;
        }
        else {
            if ( isPastSelectedTermLastAddDate ) {
                registrationTiming =
                    <div>
                        <p>The last day to register for {selectedTerm.name} was {lastAddDate.format("dddd, MMM D")}.</p>
                        <p>Please contact your campus registration office for help regarding your current schedule.</p>
                        <ul>
                            <li><a href="https://www.uwb.edu/registration" target="_blank"
                                   onClick={() => this.handleAnalyticsAdd("RegPrev-RegistrarBothellLink")}>UW Bothell</a></li>
                            <li><a href="https://registrar.washington.edu" target="_blank"
                                   onClick={() => this.handleAnalyticsAdd("RegPrev-RegistrarSeattleLink")}>UW Seattle</a></li>
                            <li><a href="http://www.tacoma.uw.edu/node/38232" target="_blank"
                                   onClick={() => this.handleAnalyticsAdd("RegPrev-RegistrarTacomaLink")}>UW Tacoma</a></li>
                        </ul>
                    </div>;
            } else {
                if ( !isEmpty(registrationDateMap) && registrationDateMap.isPeriodOne ) {
                    registrationTiming =
                        <div>
                            <p>Your estimated registration date is {formattedRegistrationDate} at 6:00 AM.</p>
                        </div>;
                } else {
                    registrationTiming =
                        <div>
                            <p>Period I Priority Registration opens on {formattedRegistrationDate}.</p>
                            <p><a href={`http://www.washington.edu/students/reg/${TermUtils.getAcademicYearCode(this.props.selectedTermId)}cal.html#Q3`}
                                  onClick={() => this.handleAnalyticsAdd("RegPrev-AcademicCalLinkClick")}
                                  target="_blank">See all Academic Calendar dates</a></p>
                        </div>;
                }
            }
        }

        return (
            <Row>
                <Col xs={12} sm={7} md={6}>
                    <Card bg={! disableButton ? "purple-light":"light-gray"} text={! disableButton ? "purple":"text-dark"}>
                        <Card.Body className="d-grid gap-3">
                            {registrationTiming}
                            {buttonComponent}
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} sm={5} md={6}>
                    <Card>
                        <Card.Body className="small">
                            <p className="mb-0"><a href="https://sdb.admin.uw.edu/students/uwnetid/register.asp"
                                            onClick={() => this.handleAnalyticsAdd("RegPrev-SpsLinkClick")}
                                            target="_blank" className="text-decoration-underline">Go to the Registration system</a> if you need to:</p>
                            <ul>
                                <li>Resolve registration errors</li>
                                <li>Add sections by SLN directly</li>
                                <li>Add sections with variable credits</li>
                                <li>Drop sections</li>
                                <li>Complete pre-registration steps</li>
                            </ul>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    }

    render() {
        return <SimpleState state={registrationDateStore.state} doneComponentBuilder={this.buildDoneComponent} name="RegistrationButton"/>
    }

}

RegistrationButton.propTypes = {
    selectedTermId: PropTypes.string.isRequired,
    hasIssues: PropTypes.bool.isRequired,
    isEmpty: PropTypes.bool.isRequired,
    hasReady: PropTypes.bool.isRequired,
    readyList: PropTypes.array.isRequired
};

export default observer(RegistrationButton);